import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress, IconButton, Tooltip, TextField, FormGroup, FormControlLabel, Checkbox, Stepper, Step, StepLabel, Alert, Paper, InputAdornment, Tabs, Tab } from "@mui/material";
import { ContentCopy, Info } from "@mui/icons-material";
import { messageShow, uploadSingle } from "../../../common/api/actions";
import { useDispatch } from "react-redux";
import { getCollections, saveImagePaths, updateIpAddress, listprojectsAndCluster, listAtlasProfiles, listAtlasOrganizations, createAtlasOrganization } from "../../api/actions/mutation";
import { getImageSource } from "../../api/routes";
import CreateOrUpdate from "../../clientConfig/CreateOrUpdate"

const COLORS = {
  primary: "#0d3559",
  secondary: "rgb(237,108,2)",
  white: "#ffffff",
  divider: "rgba(13, 53, 89, 0.1)",
};

const buttonStyles = {
  primary: {
    backgroundColor: COLORS.primary,
    "&:hover": {backgroundColor: COLORS.secondary }
  },
  secondary: {
    borderColor: COLORS.primary,
    color: COLORS.primary,
    "&:hover": {
      borderColor: COLORS.secondary,
      color: COLORS.secondary
    }
  },
};

const SampleDataEditor = ({ sampleData, onSampleDataChange, disabled }) => {
  const [localSampleData, setLocalSampleData] = useState(sampleData);

  useEffect(() => {
    setLocalSampleData(sampleData);
  }, [sampleData]);

  const handleSampleDataChange = (e) => {
    try {
      const parsedData = JSON.parse(e.target.value);
      setLocalSampleData(parsedData);
      onSampleDataChange(parsedData);
    } catch (error) {
      console.error("Invalid JSON format:", error);
    }
  };
  const handleClear = () => {
    setLocalSampleData({});
    onSampleDataChange({});
  };

  return (
    <Paper elevation={3} sx={{ mt: 3, p: 2, borderRadius: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Edit Sample Data
      </Typography>
      <TextField
        margin="dense"
        id="sample-data"
        label="Sample Data"
        fullWidth
        multiline
        rows={15}
        variant="outlined"
        value={JSON.stringify(localSampleData, null, 2)}
        onChange={handleSampleDataChange}
        disabled={disabled}
      />
      <Button
        variant="contained"
        color="secondary"
        onClick={handleClear}
        sx={{ mt: 2 }}
        disabled={disabled}
      >
        Clear Sample Data
      </Button>
    </Paper>
  );
};

const CollectionsSelector = ({ collectionsToCopy, onCollectionsChange, disabled }) => {
  const [localCollectionsToCopy, setLocalCollectionsToCopy] =
    useState(collectionsToCopy);

  useEffect(() => {
    setLocalCollectionsToCopy(collectionsToCopy);
  }, [collectionsToCopy]);

  const handleCollectionChange = (e) => {
    const { value, checked } = e.target;
    const updatedCollections = checked ? [...localCollectionsToCopy, value] : localCollectionsToCopy.filter((item) => item !== value);
    setLocalCollectionsToCopy(updatedCollections);
    onCollectionsChange(updatedCollections);
  };

  const collectionOptions = [
    "Bot",
    "Cms",
    "Faq",
    "GamifiedEvents",
    "Intents",
    "KnowledgeCategory",
    "Points",
    "PricingInput",
    "UnitType",
    "tableBookingConfig",
  ];

  return (
    <Paper elevation={3} sx={{ mt: 3, p: 2, borderRadius: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Select other Collections to Copy
      </Typography>
      <FormGroup>
        {collectionOptions.map((collection) => (
          <FormControlLabel
            key={collection}
            control={
              <Checkbox
                checked={localCollectionsToCopy.includes(collection)}
                onChange={handleCollectionChange}
                value={collection}
                disabled={disabled}
              />
            }
            label={collection}
          />
        ))}
      </FormGroup>
    </Paper>
  );
};

const PlanSelector = ({ selectedPlan, onPlanChange, disabled }) => {
  const [editingPlan, setEditingPlan] = useState(null);
  const [localSettings, setLocalSettings] = useState({});
  const [tabIndex, setTabIndex] = useState(0);

  const plans = [
    {
      label: "Basic Plan",
      description: "Kick start with the essentials.",
      settings: {
        isApplePayEnabled: false,
        isGpayEnabled: false,
        isHomeDelivery: true,
        isPickupAtStore: true,
        isUberDeliveryEnabled: false,
        isMessagingSubscribed: false,
        isAnalyticsSubscribed: false,
        isRewardsSubscribed: false,
        isLiveCounterEnabled: true,
        isTableReservationEnabled: false,
        ischatBotEnabled: false,
        isTaxAvailable: true,
        showGrowthMenu: false,
        isOrderSmsEnabled: false,
        isTableReservationSmsEnabled: false,
        isWhatsAppMessagingEnabled: false,
        isSeoOptimizationEnabled: false,
        isQrDiningEnabled: false,
        isCampaignSubscribed: false,
        isMediaGalleryEnabled: false,
        isInvoiceSubscribed: false,
        isStoreSubscribed: false,
      },
    },
    {
      label: "Growth Plan",
      description: "Level up with growth essentials.",
      settings: {
        isApplePayEnabled: true,
        isGpayEnabled: true,
        isHomeDelivery: true,
        isPickupAtStore: true,
        isUberDeliveryEnabled: true,
        isMessagingSubscribed: true,
        isAnalyticsSubscribed: false,
        isRewardsSubscribed: false,
        isLiveCounterEnabled: true,
        isTableReservationEnabled: true,
        ischatBotEnabled: false,
        isTaxAvailable: true,
        showGrowthMenu: false,
        isOrderSmsEnabled: true,
        isTableReservationSmsEnabled: true,
        isWhatsAppMessagingEnabled: false,
        isSeoOptimizationEnabled: false,
        isQrDiningEnabled: true,
        isCampaignSubscribed: true,
        isMediaGalleryEnabled: true,
        isInvoiceSubscribed: true,
        isStoreSubscribed: true,
      },
    },
    {
      label: "Scale Plan",
      description: "Scale up with multi-store.",
      settings: {
        isApplePayEnabled: true,
        isGpayEnabled: true,
        isHomeDelivery: true,
        isPickupAtStore: true,
        isUberDeliveryEnabled: true,
        isMessagingSubscribed: true,
        isAnalyticsSubscribed: true,
        isRewardsSubscribed: true,
        isLiveCounterEnabled: true,
        isTableReservationEnabled: true,
        ischatBotEnabled: true,
        isTaxAvailable: true,
        showGrowthMenu: true,
        isOrderSmsEnabled: true,
        isTableReservationSmsEnabled: true,
        isWhatsAppMessagingEnabled: true,
        isSeoOptimizationEnabled: true,
        isQrDiningEnabled: true,
        isCampaignSubscribed: true,
        isMediaGalleryEnabled: true,
        isInvoiceSubscribed: true,
        isStoreSubscribed: true,
      },
    },
  ];

  useEffect(() => {
    const plan = plans.find(p => p.label === selectedPlan);
    if (plan) {
      setEditingPlan(plan);
      setLocalSettings(plan.settings);
      onPlanChange(plan.settings, plan.label);
    }
  }, [selectedPlan]);

  const handlePlanClick = (plan) => {
    setEditingPlan(plan);
    setLocalSettings(plan.settings);
    onPlanChange(plan.settings, plan.label);
  };

  const handleSettingChange = (setting) => (event) => {
    const newSettings = {
      ...localSettings,
      [setting]: event.target.checked,
    };
    setLocalSettings(newSettings);
    if (editingPlan) {
      onPlanChange(newSettings, editingPlan.label);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const settingsCategories = [
    {
      title: "Store",
      options: [
        { key: "isStoreSubscribed", label: "Store Subscribed" },
        { key: "isTaxAvailable", label: "Tax Available" },
        { key: "isLiveCounterEnabled", label: "Live Counter" },
        { key: "isPickupAtStore", label: "Pickup at Store" },
        { key: "isHomeDelivery", label: "Home Delivery" },
        { key: "isApplePayEnabled", label: "Pay by Apple" },
        { key: "isGpayEnabled", label: "Pay by GPay" },
        { key: "isTableReservationEnabled", label: "Table Reservation" },
        { key: "isQrDiningEnabled", label: "QR Dining" },
        { key: "isUberDeliveryEnabled", label: "Uber Delivery" },
        { key: "isOrderSmsEnabled", label: "Order SMS Enabled" },
        { key: "isTableReservationSmsEnabled", label: "Table Reservation SMS Enabled" },
      ],
    },
    {
      title: "Marketing",
      options: [
        { key: "isCampaignSubscribed", label: "Campaign" },
        { key: "isMediaGalleryEnabled", label: "Media Gallery Subscription" },
        { key: "isMessagingSubscribed", label: "Messaging Subscription" },
        { key: "isRewardsSubscribed", label: "Rewards Subscription" },
        { key: "isWhatsAppMessagingEnabled", label: "WhatsApp Messaging Subscription" },
      ],
    },
    {
      title: "Analytics",
      options: [
        { key: "isAnalyticsSubscribed", label: "Analytics Subscription" },
      ],
    },
    {
      title: "Engagement",
      options: [
        { key: "showGrowthMenu", label: "Growth Menu" },
        { key: "ischatBotEnabled", label: "Chat Bot" },
      ],
    },
    {
      title: "Utilities",
      options: [
        { key: "isSeoOptimizationEnabled", label: "SEO Optimization" },
        { key: "isInvoiceSubscribed", label: "Invoices Subscription" },
      ],
    },
  ];

  return (
    <Box sx={{ display: "grid", gap: 2, width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 1000,
          p: 2,
          width: "100%",
        }}
      >
        {plans.map((plan) => (
          <Paper
            key={plan.label}
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 2,
              textAlign: "center",
              border:
                selectedPlan === plan.label
                  ? `2px solid ${COLORS.secondary}`
                  : "1px solid rgba(13, 53, 89, 0.1)",
              cursor: disabled ? "default" : "pointer",
              backgroundColor: selectedPlan === plan.label ? "rgba(237,108,2,0.1)" : "white",
              opacity: disabled ? 0.7 : 1,
            }}
            onClick={() => {
              if (!disabled) {
                handlePlanClick(plan);
              }
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {plan.label}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {plan.description}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Configure Settings:
        </Typography>
        <Box sx={{ borderBottom: "2px solid #e0e0e0", mb: 2, width: "100%" }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            aria-label="settings categories tabs"
            sx={{
              width: "100%",
              minHeight: "48px",
              "& .MuiTabs-indicator": {
                backgroundColor: "#1976d2",
                height: "2px",
              },
              "& .MuiTab-root": {
                fontWeight: 500,
                textTransform: "none",
                fontSize: "16px",
                padding: "12px 24px",
                color: "#666",
                minWidth: "auto",
                opacity: 1,
                "&.Mui-selected": {
                  color: "#1976d2",
                },
              },
            }}
          >
            {settingsCategories.map((category, index) => (
              <Tab key={category.title} label={category.title} />
            ))}
          </Tabs>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,
            p: 1,
            width: "100%",
          }}
        >
          {settingsCategories[tabIndex].options.map((option) => (
            <FormControlLabel
              key={option.key}
              sx={{
                gridColumn: option.key === "isStoreSubscribed" ? "1 / -1" : "auto",
                borderBottom: option.key === "isStoreSubscribed" ? "1px solid rgba(0,0,0,0.1)" : "none",
                pb: option.key === "isStoreSubscribed" ? 1 : 0,
                mb: option.key === "isStoreSubscribed" ? 1 : 0,
              }}
              control={
                <Checkbox
                  checked={!!localSettings[option.key]}
                  onChange={handleSettingChange(option.key)}
                  disabled={disabled}
                  sx={{
                    color: COLORS.primary,
                    "&.Mui-checked": {
                      color: COLORS.secondary,
                    },
                  }}
                />
              }
              label={option.label}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const ImageUploader = ({ collections, onUpload, disabled }) => {
  return (
    <Box sx={{ mt: 2, mb: 3, width: "100%" }}>
      {Object.entries(collections).map(([collection, items]) => (
        <Paper
          key={collection}
          elevation={3}
          sx={{ mt: 3, p: 2, borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {collection}
          </Typography>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 }}
          >
            {items.length > 0 ? (
              items.map((item) => (
                <Box
                  key={item._id}
                  sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                >
                  <Typography variant="subtitle1">
                    {item.name || item.rewardName || "Untitled"}
                  </Typography>
                  <Box
                    sx={{ mt: 1, mb: 1, width: "100%", height: "200px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden" }}
                  >
                    <img
                      src={item.previewImage || getImageSource(item.image) || getImageSource()}
                      alt={""}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const fileReader = new FileReader();
                          fileReader.onload = () => onUpload(collection, item._id, file, fileReader.result);
                          fileReader.readAsDataURL(file);
                        }
                      }}
                      disabled={disabled}
                      style={{ display: "none" }}
                      id={`file-${collection}-${item._id}`}
                    />
                    <label htmlFor={`file-${collection}-${item._id}`}>
                      <Button variant="outlined" component="span" disabled={disabled}>
                        {disabled ? "Uploading..." : "Select Image"}
                      </Button>
                    </label>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, height: "100px" }}>
                <Typography variant="subtitle1" color="textSecondary">
                  No Images
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const OnboardingPopup = ({
  open,
  onClose,
  targetProspect,
  onSubmit,
  mongoUser,
  mongoPassword,
  destinationUri,
  onSaveSampleData,
  onUpdateSettings,
  sampleData,
  loader,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [localSampleData, setLocalSampleData] = useState(sampleData);
  const [collectionsToCopy, setCollectionsToCopy] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedCluster, setSelectedCluster] = useState("");
  const [settings, setSettings] = useState({
    isTableReservationEnabled: false,
    isUberDeliveryEnabled: false,
    showGrowthMenu: false,
    isAnalyticsSubscribed: false,
    isMessagingSubscribed: false,
    isRewardsSubscribed: false,
    isLiveCounterEnabled: false,
    isPickupAtStore: false,
    isHomeDelivery: false,
    isQrDiningEnabled: false,
    isCampaignSubscribed: false,
    isMediaGalleryEnabled: false,
    isInvoiceSubscribed: false,
    isStoreSubscribed: false,
  });
  const [selectedPlan, setSelectedPlan] = useState("Basic Plan");
  const [currentStage, setCurrentStage] = useState(1);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [collections, setCollections] = useState({});
  const [uploadedImages, setUploadedImages] = useState({});
  const [showUpdateIPButton, setShowUpdateIPButton] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [projectsAndClusters, setProjectsAndClusters] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [hasFetchedProjects, setHasFetchedProjects] = useState(false);
  const [atlasProfiles, setAtlasProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [atlasOrganizations, setAtlasOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const dispatch = useDispatch();

  const plans = [
    {
      label: "Basic Plan",
      description: "Kick start with the essentials.",
      settings: {
        isApplePayEnabled: false,
        isGpayEnabled: false,
        isHomeDelivery: true,
        isPickupAtStore: true,
        isUberDeliveryEnabled: false,
        isMessagingSubscribed: false,
        isAnalyticsSubscribed: false,
        isRewardsSubscribed: false,
        isLiveCounterEnabled: true,
        isTableReservationEnabled: false,
        ischatBotEnabled: false,
        isTaxAvailable: true,
        showGrowthMenu: false,
        isOrderSmsEnabled: false,
        isTableReservationSmsEnabled: false,
        isWhatsAppMessagingEnabled: false,
        isSeoOptimizationEnabled: false,
        isQrDiningEnabled: false,
        isCampaignSubscribed: false,
        isMediaGalleryEnabled: false,
        isInvoiceSubscribed: false,
        isStoreSubscribed: false,
      },
    },
    {
      label: "Growth Plan",
      description: "Level up with growth essentials.",
      settings: {
        isApplePayEnabled: true,
        isGpayEnabled: true,
        isHomeDelivery: true,
        isPickupAtStore: true,
        isUberDeliveryEnabled: true,
        isMessagingSubscribed: true,
        isAnalyticsSubscribed: false,
        isRewardsSubscribed: false,
        isLiveCounterEnabled: true,
        isTableReservationEnabled: true,
        ischatBotEnabled: false,
        isTaxAvailable: true,
        showGrowthMenu: false,
        isOrderSmsEnabled: true,
        isTableReservationSmsEnabled: true,
        isWhatsAppMessagingEnabled: false,
        isSeoOptimizationEnabled: false,
        isQrDiningEnabled: true,
        isCampaignSubscribed: true,
        isMediaGalleryEnabled: true,
        isInvoiceSubscribed: true,
        isStoreSubscribed: true,
      },
    },
    {
      label: "Scale Plan",
      description: "Scale up with multi-store.",
      settings: {
        isApplePayEnabled: true,
        isGpayEnabled: true,
        isHomeDelivery: true,
        isPickupAtStore: true,
        isUberDeliveryEnabled: true,
        isMessagingSubscribed: true,
        isAnalyticsSubscribed: true,
        isRewardsSubscribed: true,
        isLiveCounterEnabled: true,
        isTableReservationEnabled: true,
        ischatBotEnabled: true,
        isTaxAvailable: true,
        showGrowthMenu: true,
        isOrderSmsEnabled: true,
        isTableReservationSmsEnabled: true,
        isWhatsAppMessagingEnabled: true,
        isSeoOptimizationEnabled: true,
        isQrDiningEnabled: true,
        isCampaignSubscribed: true,
        isMediaGalleryEnabled: true,
        isInvoiceSubscribed: true,
        isStoreSubscribed: true,
      },
    },
  ];

  useEffect(() => {
    fetchAtlasProfiles();
  }, []);

  const fetchAtlasProfiles = async () => {
    try {
      const response = await listAtlasProfiles();
      if (response.data.success) {
        setAtlasProfiles(response.data.data);
        const savedProfile = localStorage.getItem("atlas_profile");
        if (savedProfile && response.data.data.includes(savedProfile)) {
          setSelectedProfile(savedProfile);
        } else if (response.data.data.length > 0) {
          setSelectedProfile(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching Atlas profiles:", error);
    }
  };

  useEffect(() => {
    if (selectedProfile) {
      fetchAtlasOrganizations(selectedProfile);
    } else {
      setAtlasOrganizations([]);
      setOrgId("");
    }
  }, [selectedProfile]);

  const fetchAtlasOrganizations = async (profile, preSelectedId) => {
    setLoadingOrgs(true);
    try {
      const response = await listAtlasOrganizations(profile);
      if (response.data.success) {
        setAtlasOrganizations(response.data.data);
        if (preSelectedId) {
          setOrgId(preSelectedId);
          localStorage.setItem("org_Id", preSelectedId);
        } else {
          const savedOrgId = localStorage.getItem("org_Id");
          if (savedOrgId && response.data.data.some(org => org.id === savedOrgId)) {
            setOrgId(savedOrgId);
          } else if (response.data.data.length > 0) {
            setOrgId(response.data.data[0].id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching Atlas organizations:", error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName) return;
    setIsLoading(true);
    try {
      const response = await createAtlasOrganization({ profile: selectedProfile, name: newOrgName });
      if (response.data.success) {
        dispatch(messageShow("Organization created successfully!"));
        const newOrg = response.data.data;
        await fetchAtlasOrganizations(selectedProfile, newOrg.id);
        setNewOrgName("");
      }
    } catch (error) {
      console.error("Error creating organization:", error);
      dispatch(messageShow("Failed to create organization"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && targetProspect) {
      resetState();
      const savedOrgId = localStorage.getItem("org_Id");
      if (savedOrgId) {
        setOrgId(savedOrgId);
      }
    }
  }, [open, targetProspect]);

  useEffect(() => {
    if (open) {
      setLocalSampleData(sampleData);
    }
  }, [open, sampleData]);

  useEffect(() => {
    if (currentStage === 4) {
      fetchCollections();
    }
  }, [currentStage]);

  const resetState = () => {
    // setEnvType("");
    setLocalSampleData(sampleData);
    setCollectionsToCopy([]);
    setSelectedProject("");
    setSelectedCluster("");
    setSettings({
      isTableReservationEnabled: false,
      isUberDeliveryEnabled: false,
      showGrowthMenu: false,
      isAnalyticsSubscribed: false,
      isMessagingSubscribed: false,
      isRewardsSubscribed: false,
      isLiveCounterEnabled: false,
      isPickupAtStore: false,
      isHomeDelivery: false,
      isQrDiningEnabled: false,
      isCampaignSubscribed: false,
      isMediaGalleryEnabled: false,
      isInvoiceSubscribed: false,
      isStoreSubscribed: false,
    });
    setSelectedPlan("Basic Plan");
    setCurrentStage(1);
    setOnboardingCompleted(false);
    setCollections({});
    setUploadedImages({});
    setOrgId("");
    setProjectsAndClusters([]);
    setLoadingProjects(false);
    setHasFetchedProjects(false);
    setAtlasOrganizations([]);
    setNewOrgName("");
    setSelectedProfile("");
  };

  const handleConfirm = async () => {
    if (!targetProspect || !targetProspect._id) {
      console.error("Required information is missing.");
      return;
    }
    setIsLoading(true);
    await onSubmit({
      prospectName: targetProspect.storeName,
      projectId: selectedProject,
      clusterId: selectedCluster,
      orgId,
      profile: selectedProfile
    });
    setIsLoading(false);
    setCurrentStage(3);
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        dispatch(messageShow("URL copied to clipboard!"));
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleClose = () => {
    if (onboardingCompleted) {
      onClose();
      window.location.reload();
    }
    if (!isLoading) onClose();
  };

  const handleSampleDataChange = (data) => {
    setLocalSampleData(data);
  };

  const handleCollectionsChange = (collections) => {
    setCollectionsToCopy(collections);
  };

  const handlePlanChange = (newSettings, planLabel) => {
    setSettings(newSettings);
    setSelectedPlan(planLabel);
  };

  const handleSaveSampleData = async () => {
    setIsLoading(true);
    try {
      const response = await onSaveSampleData({
        destinationUri,
        sampleData: settings.isStoreSubscribed ? localSampleData : {},
        collectionsToCopy,
        storeName: targetProspect.storeName,
      });

      const subscribedPlan = response?.data?.subscribedPlan;
      const folderPath = response?.data?.folderPath;

      if (subscribedPlan) {
        const planMap = {
          Basic: 'Basic Plan',
          Growth: 'Growth Plan',
          Scale: 'Scale Plan'
        };
        const planLabel = planMap[subscribedPlan];

        if (planLabel) {
          const plan = plans.find(p => p.label === planLabel);

          if (plan) {
            setSettings(plan.settings);
            setSelectedPlan(planLabel);
          }
        }
      }
      if (folderPath) targetProspect.folderPath = folderPath;
      if (settings.isStoreSubscribed) {
        setCurrentStage(4);
      } else {
        setCurrentStage(5);
      }
    } catch (error) {
      console.error("Error saving sample data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    setIsLoading(true);
    try {
      const response = await onUpdateSettings({
        destinationUri,
        settings,
        storeName: targetProspect.storeName,
      });
      
      if (response.errorName === "MongoServerSelectionError") {
        setShowUpdateIPButton(true);
        dispatch(messageShow(response.error));
        return;
      }
      
      if (response.errorName !== "MongoServerSelectionError") {
        setCurrentStage(2);
      }
      setShowUpdateIPButton(false);
    } catch (error) {
      console.error("Error updating settings:", error);
      dispatch(messageShow(error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectChange = (event) => {
    const projectId = event.target.value;
    setSelectedProject(projectId);
    if (projectId) {
      const selectedProjectData = projectsAndClusters.find(p => p.projectId === projectId);
      if (selectedProjectData && selectedProjectData.clusters.length > 0) {
        setSelectedCluster(selectedProjectData.clusters[0].clusterId);
      }
    } else {
      setSelectedCluster("");
    }
  };

  const handleClusterChange = (event) => {
    setSelectedCluster(event.target.value);
  };

  const getSelectedClusterConnectionString = () => {
    if (!selectedProject || !selectedCluster) return "";
    const project = projectsAndClusters.find(p => p.projectId === selectedProject);
    if (!project) return "";
    const cluster = project.clusters.find(c => c.clusterId === selectedCluster);
    return cluster ? cluster.connectionString : "";
  };

  const fetchCollections = async () => {
    setIsLoading(true);
    const destDbName = targetProspect.storeName
      .toLowerCase()
      .replace(/ /g, "_")
      .replace(/[^a-z0-9_]/g, "");
    try {
      const response = await getCollections({
        destinationUri,
        collections: ["Banner", "Product", "Category", "Rewards", "Goal"],
        destDbName,
      });
      if (response.data && response.data.success) {
        const transformedCollections = {};
        if (response.data.errorName === "MongoServerSelectionError") {
          setShowUpdateIPButton(true);
          return dispatch(messageShow(response.data.error))
        }
        setShowUpdateIPButton(false);
        Object.entries(response.data.data).forEach(
          ([collectionName, items]) => {
            transformedCollections[collectionName] = items.map((item) => ({
              ...item,
              name: item.name ||
              item.goalName || item.rewardName || "Untitled",
              image: item.image || "",
            }));
          }
        );
        setCollections(transformedCollections);
      } else {
        console.error("Invalid response format:", response);
        dispatch(
          messageShow("Failed to fetch collections: Invalid response format")
        );
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
      dispatch(messageShow(error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (collection, id, file, previewImage) => {
    setIsLoading(true);
    try {
      setCollections((prevCollections) => ({
        ...prevCollections,
        [collection]: prevCollections[collection].map((item) =>
          item._id === id ? { ...item, previewImage } : item
        ),
      }));

      const formData = new FormData();
      formData.append("type", "onboarding");
      formData.append("name", `${id}`);
      formData.append("collection", collection.toLowerCase());
      formData.append("file", file);
      formData.append("folder", targetProspect.folderPath || "");

      const { data } = await uploadSingle(formData);
      if (data?.success) {
        setUploadedImages((prevImages) => ({
          ...prevImages,
          [collection]: {
            ...prevImages[collection],
            [id]: data.file,
          },
        }));
        setCollections((prevCollections) => ({
          ...prevCollections,
          [collection]: prevCollections[collection].map((item) =>
            item._id === id ? { ...item, image: data.file } : item
          ),
        }));
        dispatch(messageShow(`Image uploaded successfully for ${collection}.`));
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      dispatch(messageShow(`Failed to upload image for ${collection}`));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteImageUpload = async () => {
    setIsLoading(true);
    try {
      const response = await saveImagePaths(
        {
          destinationUri,
          imageData: uploadedImages,
          destDbName: targetProspect.storeName
            .toLowerCase()
            .replace(/ /g, "_")
            .replace(/[^a-z0-9_]/g, ""),
        },
        dispatch
      );
      if (response?.data?.success) {
        dispatch(messageShow("Images saved successfully"));
        setCurrentStage(5);
      }
    } catch (error) {
      console.error("Error saving image paths:", error);
      dispatch(messageShow("Failed to save image paths"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateIp = async () => {
    const projectId = selectedProject;
    if (!projectId) {
      console.error("Project ID is missing.");
      return;
    }

    try {
      setIsLoading(true);
      await updateIpAddress({ projectId, profile: selectedProfile });
      dispatch(messageShow("IP address updated successfully."));
      setShowUpdateIPButton(false);
      if (currentStage === 4) fetchCollections();
      if (currentStage === 3) {
        await handleUpdateSettings(); // Retry updating settings after IP update
      }
    } catch (error) {
      console.error("Error updating IP address:", error);
      dispatch(messageShow("Failed to update IP address."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchProjects = async () => {
    if (!orgId) return;
    setLoadingProjects(true);
    try {
      const response = await listprojectsAndCluster({ orgId, profile: selectedProfile });
      if (response.data.success) {
        setProjectsAndClusters(response.data.data);
        setHasFetchedProjects(true);
        localStorage.setItem("org_Id", orgId);
        localStorage.setItem("atlas_profile", selectedProfile);
      } else {
         dispatch(messageShow(response.data.message || "Failed to fetch projects"));
      }
    } catch (error) {
      console.error("Error fetching projects and clusters:", error);
      dispatch(messageShow("Failed to fetch projects and clusters"));
    } finally {
      setLoadingProjects(false);
    }
  };

  const renderStageContent = () => {
    if (onboardingCompleted) {
      return (
        <Box
          sx={{
            mt: 10,
            mb: 3,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Alert severity="success" sx={{ mb: 2, width: "fit-content" }}>
              Onboarding completed successfully!
            </Alert>
          </Box>
          <Typography variant="body1" align="center">
            The store has been successfully onboarded with all the configured
            settings.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }} align="center">
            <strong>MongoDB URI:</strong> {destinationUri}
            <Tooltip title="Copy">
              <IconButton
                size="small"
                onClick={() => handleCopy(destinationUri)}
                sx={{ ml: 1 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", mt: 3, gap: 2 }}
        >
          <CircularProgress size={40} sx={{ color: COLORS.primary }} />
          <Typography variant="body1" color="textSecondary">
            {currentStage === 1 ? "Confirming Database setup..." : currentStage === 3 ? "Updating Subscription details..." : currentStage === 2 ? "Saving Default data..." : "Loading..."}
          </Typography>
        </Box>
      );
    }

    switch (currentStage) {
      case 1:
        return (
          <Box sx={{ mt: 2, mb: 3, width: "100%" }}>
            <TextField
              margin="dense"
              id="prospect-name"
              label="Prospect"
              fullWidth
              variant="outlined"
              value={`${targetProspect.storeName}`}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                  <InputLabel id="profile-select-label">Atlas Profile</InputLabel>
                  <Select
                    labelId="profile-select-label"
                    id="profile-select"
                    value={selectedProfile}
                    label="Atlas Profile"
                    onChange={(e) => setSelectedProfile(e.target.value)}
                    disabled={loadingProjects || projectsAndClusters.length > 0}
                  >
                    {atlasProfiles.map((profile) => (
                      <MenuItem key={profile} value={profile}>
                        {profile}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                  <InputLabel id="org-select-label">Organization</InputLabel>
                  <Select
                    labelId="org-select-label"
                    id="org-select"
                    value={orgId}
                    label="Organization"
                    onChange={(e) => {
                      setOrgId(e.target.value);
                      if (e.target.value !== "new") {
                        setNewOrgName("");
                      }
                    }}
                    disabled={loadingOrgs || loadingProjects || projectsAndClusters.length > 0 || !selectedProfile}
                  >
                    <MenuItem value="new">
                      <em>+ Create New Organization</em>
                    </MenuItem>
                    {atlasOrganizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name} ({org.id})
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingOrgs && <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: 18 }} />}
                </FormControl>
                {orgId === "new" && (
                   <Box sx={{ display: 'flex', gap: 2, mt: 1, width: '100%', alignItems: 'center' }}>
                     <TextField
                       margin="dense"
                       id="new-org-name"
                       label="New Organization Name"
                       fullWidth
                       variant="outlined"
                       value={newOrgName}
                       onChange={(e) => setNewOrgName(e.target.value)}
                       placeholder="e.g. My New Org"
                     />
                     <Button
                       variant="contained"
                       onClick={handleCreateOrganization}
                       disabled={!newOrgName || isLoading}
                       sx={{ ...buttonStyles.primary, height: '54px' }}
                     >
                       Create
                     </Button>
                   </Box>
                )}
                 <Button
                     variant="contained"
                     onClick={handleFetchProjects}
                     disabled={!orgId || orgId === "new" || !selectedProfile || loadingProjects || projectsAndClusters.length > 0}
                     sx={{ ...buttonStyles.primary, height: '54px', mt: 1 }}
                 >
                     {loadingProjects ? <CircularProgress size={24} color="inherit" /> : "Fetch"}
                 </Button>
                 {projectsAndClusters.length > 0 && (
                    <Button
                        variant="outlined"
                         onClick={() => {
                            setProjectsAndClusters([]);
                            // setOrgId("");
                            setSelectedProject("");
                            setSelectedCluster("");
                         }}
                         sx={{ ...buttonStyles.secondary, height: '54px', mt: 1 }}
                    >
                        Change
                    </Button>
                 )}
            </Box>
            {loadingProjects ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 3,
                  gap: 2,
                }}
              >
                <CircularProgress size={40} sx={{ color: COLORS.primary }} />
                <Typography variant="body1" color="textSecondary">
                  Loading projects and clusters...
                </Typography>
              </Box>
            ) : hasFetchedProjects ? (
              <>
                <FormControl fullWidth variant="outlined" sx={{ mt: 3 }}>
                  <InputLabel id="project-select-label">
                    Project
                  </InputLabel>
                  <Select
                    labelId="project-select-label"
                    id="project-select"
                    value={selectedProject}
                    label="Project"
                    onChange={handleProjectChange}
                    disabled={isLoading || loader}
                  >
                    <MenuItem value="">
                      <em>Create New Project</em>
                    </MenuItem>
                    {projectsAndClusters.map((project) => (
                      <MenuItem
                        key={project.projectId}
                        value={project.projectId}
                      >
                        {project.projectName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth variant="outlined" sx={{ mt: 3 }}>
                  <InputLabel id="cluster-select-label">
                    Cluster
                  </InputLabel>
                  <Select
                    labelId="cluster-select-label"
                    id="cluster-select"
                    value={selectedCluster}
                    label="Cluster"
                    onChange={handleClusterChange}
                    disabled={isLoading || loader || (selectedProject && !projectsAndClusters.find(p => p.projectId === selectedProject)?.clusters.length)}
                  >
                    <MenuItem value="">
                      <em>Create New Cluster</em>
                    </MenuItem>
                    {selectedProject &&
                      projectsAndClusters
                        .find((p) => p.projectId === selectedProject)
                        ?.clusters.map((cluster) => (
                          <MenuItem
                            key={cluster.clusterId}
                            value={cluster.clusterId}
                          >
                            {cluster.clusterName}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
                {selectedCluster && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      Selected Cluster Connection String:
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                      {getSelectedClusterConnectionString()}
                    </Typography>
                  </Box>
                )}
              </>
            ) : null}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2, mb: 3, width: "100%" }}>
            <Box sx={{ mt: 3 }}>
              {destinationUri && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body1" sx={{ mr: 1 }}>
                    <strong>Mongodb URL:</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ flexGrow: 1, wordBreak: "break-all" }}>
                    {destinationUri}
                  </Typography>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(destinationUri)}
                      sx={{ ml: 1 }}
                      disabled={isLoading || loader}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {settings.isStoreSubscribed && localSampleData && (
                <SampleDataEditor
                  sampleData={localSampleData}
                  onSampleDataChange={handleSampleDataChange}
                  disabled={isLoading || loader}
                />
              )}
              <CollectionsSelector
                collectionsToCopy={collectionsToCopy}
                onCollectionsChange={handleCollectionsChange}
                disabled={isLoading || loader}
              />
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 2, mb: 3, width: "100%" }}>
            {showUpdateIPButton ? (
              renderUpdateIp()
            ) : (
              <PlanSelector
                selectedPlan={selectedPlan}
                onPlanChange={handlePlanChange}
                disabled={isLoading || loader}
              />
            )}
          </Box>
        );
      case 4:
        return (
          <Box sx={{ mt: 2, mb: 3, width: "100%" }}>
            <ImageUploader
              collections={collections}
              onUpload={handleImageUpload}
              disabled={isLoading || loader}
            />
            {showUpdateIPButton && renderUpdateIp()}
          </Box>
        );
      case 5:
        return (
          <Box sx={{ mt: 2, mb: 3, width: "100%", height: "100%" }}>
            <CreateOrUpdate
              clientId={null}
              crmId={targetProspect._id}
              _id={targetProspect.clientConfigId}
              prefillFolderPath={targetProspect.folderPath}
              prefillDbConnectionURL={destinationUri}
              isModal={true}
              onSuccess={() => setOnboardingCompleted(true)}
              onCancel={() => setCurrentStage(settings.isStoreSubscribed ? 4 : 2)}
              onClose={handleClose}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const renderUpdateIp = () => {
    return (
      <Button
        variant="contained"
        sx={buttonStyles.primary}
        onClick={() => {
          handleUpdateIp();
        }}
        disabled={isLoading || loader}
      >
        Update IP
      </Button>
    )
  }

  const renderDialogActions = () => {
    if (onboardingCompleted) {
      return (
        <Button onClick={handleClose} variant="contained" sx={buttonStyles.primary}>
          Close
        </Button>
      );
    }

    switch (currentStage) {
      case 1:
        return (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={isLoading || loader || loadingProjects}
              sx={buttonStyles.secondary}
            >
              Close
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={buttonStyles.primary}
              disabled={isLoading || loader || loadingProjects}
            >
              Confirm
            </Button>
          </>
        );
      case 3:
        return (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isLoading || loader}
              sx={buttonStyles.secondary}
            >
              Close
            </Button>
            <Button onClick={() => setCurrentStage(1)} variant="outlined" sx={buttonStyles.secondary} disabled={isLoading || loader}>
              Back
            </Button>
            <Button
              onClick={handleUpdateSettings}
              variant="contained"
              sx={buttonStyles.primary}
              disabled={isLoading || loader}
            >
              Update Plan
            </Button>
          </>
        );
      case 2:
        return (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={isLoading || loader}
              sx={buttonStyles.secondary}
            >
              Close
            </Button>
            <Button onClick={() => setCurrentStage(3)} variant="outlined" sx={buttonStyles.secondary} disabled={isLoading || loader}>
              Back
            </Button>
            <Button
              onClick={handleSaveSampleData}
              variant="contained"
              sx={buttonStyles.primary}
              disabled={isLoading || loader}
            >
              Save Sample Data
            </Button>
          </>
        );
      case 4:
        return (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isLoading || loader}
              sx={buttonStyles.secondary}
            >
              Close
            </Button>
            <Button onClick={() => setCurrentStage(2)} variant="outlined" sx={buttonStyles.secondary} disabled={isLoading || loader}>
              Back
            </Button>
            <Button
              onClick={handleCompleteImageUpload}
              variant="contained"
              disabled={isLoading || loader}
              sx={buttonStyles.primary}
            >
              Save
            </Button>
          </>
        );
      case 5:
        return null;
      default:
        return null;
    }
  };

  const steps = [
    "Database setup",
    "Subscription plan",
    "Default data setup",
    settings.isStoreSubscribed ? "Basic Store Setup" : null,
    "Client Configuration",
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          borderRadius: 2,
          boxShadow: `0px 4px 20px ${COLORS.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          backgroundColor: COLORS.primary,
          color: COLORS.white,
          py: 1,
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
          Onboarding Process
        </Typography>
        <Stepper
          activeStep={
            {
              1: 0,
              3: 1,
              2: 2,
              4: 3,
              5: settings.isStoreSubscribed ? 4 : 3,
            }[currentStage] || 0
          }
          alternativeLabel
          sx={{
            mt: 1,
            "& .MuiStepLabel-root .Mui-completed": {
              color: COLORS.secondary,
            },
            "& .MuiStepLabel-root .Mui-active": {
              color: COLORS.secondary,
            },
            "& .MuiStepLabel-label": {
              color: COLORS.white,
              "&.Mui-active": {
                color: COLORS.white,
              },
              "&.Mui-completed": {
                color: COLORS.white,
              },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flex: 1,
          py: 2,
          maxHeight: "60vh",
          overflowY: "auto",
          width: "100%",
        }}
      >
        {renderStageContent()}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          gap: 2,
        }}
      >
        {renderDialogActions()}
      </DialogActions>
    </Dialog>
  );
};

OnboardingPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  targetProspect: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    storeName: PropTypes.string,
    contactName: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  mongoUser: PropTypes.string,
  mongoPassword: PropTypes.string,
  destinationUri: PropTypes.string,
  onSaveSampleData: PropTypes.func.isRequired,
  onUpdateSettings: PropTypes.func.isRequired,
  sampleData: PropTypes.object,
  loader: PropTypes.bool,
};

export default OnboardingPopup;